import moment from "moment-timezone";
import { ref } from "vue";
import { useStore } from "vuex";
import { customDateFormatWithTimezone } from "@/lib/helpers";

export interface Timeslot {
  arrival_window_end: string;
  arrival_window_start: string;
  disabled: boolean;
  scheduled_time: string;
}

export const useAppointmentAvailability = () => {
  const store = useStore();

  const selectedTimeSlot = ref<Timeslot | null>(null);

  const disabledDates = ref<Date[]>([]);
  const timeSlots = ref<Timeslot[]>([]);
  const queriedMonths = ref<Date[]>([]);
  const loadingTimeSlots = ref(false);
  // Slot fetches overlap (mount, technician changes, date picks) and the
  // multi-technician one is slow, so an older response can land after a newer
  // one. Only the latest request may write.
  let latestSlotsRequest = 0;

  const selectTimeSlot = (timeslot: Timeslot) => {
    selectedTimeSlot.value = timeslot;
  };
  const buildTechParams = (data: {
    userId?: number;
    userIds?: number[];
  }): Record<string, unknown> => {
    if (data.userIds && data.userIds.length > 1) {
      return { user_ids: data.userIds };
    }
    const singleId =
      data.userIds && data.userIds.length === 1 ? data.userIds[0] : data.userId;
    return singleId ? { user_id: singleId } : {};
  };

  const getMonthAvailability = async (data: {
    month: Date;
    serviceCategoryId: number;
    durationMinutes: number;
    userId?: number;
    userIds?: number[];
    zipcode: string;
  }) => {
    // cache month requests
    if (!queriedMonths.value.includes(data.month)) {
      queriedMonths.value.push(data.month);
      const res = await store.dispatch("fetchMonthAvailability", {
        service_date: data.month,
        service_category_id: data.serviceCategoryId,
        duration_minutes: data.durationMinutes,
        ...buildTechParams(data),
        postal_code: data.zipcode
      });
      const dates = Object.keys(res.data)
        .filter((date) => !res.data[date])
        .map((date) => moment(date).toDate());
      disabledDates.value = disabledDates.value.concat(dates);
    }
  };
  const getDateTimeSlots = async (data: {
    date: Date;
    serviceCategoryId: number;
    durationMinutes: number;
    userId?: number;
    userIds?: number[];
    zipcode: string;
  }) => {
    const requestId = ++latestSlotsRequest;
    loadingTimeSlots.value = true;
    const res = await store.dispatch("fetchTimeSlots", {
      service_date: data.date,
      service_category_id: data.serviceCategoryId,
      duration_minutes: data.durationMinutes,
      ...buildTechParams(data),
      postal_code: data.zipcode,
      all_hours: true
    });
    if (requestId !== latestSlotsRequest) return;
    timeSlots.value = res.data;
    loadingTimeSlots.value = false;
  };
  const isPast = (date: string) => moment(date).isBefore(moment());
  const getTimeSlotLabel = (timeslot: Timeslot) => {
    return `${customDateFormatWithTimezone(
      timeslot.arrival_window_start,
      "ha"
    )} - ${customDateFormatWithTimezone(timeslot.arrival_window_end, "ha")}`;
  };
  const clearCache = () => {
    queriedMonths.value = [];
    disabledDates.value = [];
  };

  return {
    disabledDates,
    timeSlots,
    getTimeSlotLabel,
    selectTimeSlot,
    selectedTimeSlot,
    isPast,
    getMonthAvailability,
    getDateTimeSlots,
    clearCache
  };
};
